import { cva } from 'class-variance-authority'

import type { ButtonHTMLAttributes, HTMLAttributes, JSX, ReactNode } from 'react'

/**
 * The pill label behind production's `.chip` and `.nchip` — one component, since every override
 * layer styles them together and they render identically. `.chip.cob`, `.chip.tint` and `.chip.on`
 * share one declaration block and become `accent`; the dead `.hairline` modifier is dropped.
 *
 * Measured on production: 27px tall, 4px/8px padding, 6px gap, 14px icons. `interactive` is the
 * starter chip: a real `<button>` with the 44px touch target, so it is a boolean rather than a size.
 * Class strings must stay in this `.tsx` file — Tailwind's `@source` does not scan `.ts`.
 */
const chip = cva(
  [
    'inline-flex items-center gap-[6px] min-h-[27px] px-[8px] py-[4px] rounded-full border-0',
    'font-sans font-medium text-3xs leading-tight text-ink',
    '[&>img]:size-[14px] [&>img]:object-contain',
  ],
  {
    variants: {
      variant: { default: 'bg-fill-subtle', accent: 'bg-fill-strong' },
      interactive: {
        // Production declares a colour transition here and no hover; the ring is added, not measured.
        true: [
          'min-h-[44px] cursor-pointer transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        ],
        false: '',
      },
    },
    defaultVariants: { variant: 'default', interactive: false },
  },
)

type Common = { variant?: 'default' | 'accent'; className?: string; children: ReactNode }

type LabelProps = Common & Omit<HTMLAttributes<HTMLSpanElement>, keyof Common> & { interactive?: false }

type PressableProps = Common & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof Common> & { interactive: true }

export type ChipProps = LabelProps | PressableProps

export default function Chip(props: ChipProps): JSX.Element {
  const { variant, interactive, className, ...rest } = props
  const classes = chip({ variant, interactive, className })

  if (interactive)
    return <button type="button" {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)} className={classes} />

  return <span {...(rest as HTMLAttributes<HTMLSpanElement>)} className={classes} />
}
